import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Flattens markdown block structure (paragraphs, lists, headings) into inline
 * text so a CSS line-clamp on the wrapping element truncates cleanly, while
 * still honoring inline emphasis (bold/italic).
 */
// Block-level nodes (p, headings, lists) are separated by a blank line in the
// markdown source — CommonMark treats that as a real paragraph break, so we
// render an explicit <br/> between them. A single newline *inside* a block
// (e.g. mid-paragraph, or between list items) is a soft break and collapses
// to a space, same as plain-text/HTML whitespace handling.
const inlineMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    h1: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    h2: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    h3: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    ul: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    ol: ({ children }: { children?: React.ReactNode }) => <>{children}<br /></>,
    li: ({ children }: { children?: React.ReactNode }) => <>{children}; </>,
    a: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    br: () => <> </>,
};

export interface MarkdownCardPreviewProps {
    text: string;
    className?: string;
}

/**
 * Renders a markdown description as a single clamped block of inline text,
 * used for card previews (e.g. OperatorsMarketplace, IntelligenceHub). Pair
 * the `className` with a `line-clamp-*` utility to control how many lines show.
 */
const MarkdownCardPreview: React.FC<MarkdownCardPreviewProps> = ({ text, className }) => {
    return (
        <p className={className}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineMarkdownComponents}>
                {text}
            </ReactMarkdown>
        </p>
    );
};

export default MarkdownCardPreview;
